<template>
  <v-app class="app-background">
    <v-container class="fill-height" fluid>
      <v-row align="center" justify="center">
        <v-col cols="12" sm="8" md="6" lg="4">
          <v-card class="pa-6 pb-8 professional-card" elevation="6" rounded="xl">
            <div class="text-center mb-6">
              <v-img class="mx-auto mb-4" max-width="100" src="./logo.png" contain></v-img>
              <h1 class="text-h5 font-weight-medium primary--text">Welcome to {{ appName }}</h1>
              <p class="text-body-2 grey--text text--darken-1 mt-1">Sign in to manage your invoices</p>
            </div>

            <v-card-text class="px-4">
              <!-- Email Input -->
              <v-text-field
                v-model="email"
                label="Email Address"
                type="email"
                :rules="emailRules"
                outlined
                clearable
                prepend-inner-icon="mdi-email-outline"
                class="rounded-lg mb-4"
                background-color="grey lighten-4"
              ></v-text-field>

              <!-- Password Input (for Email + Password) -->
              <v-text-field
                v-if="showPassword"
                v-model="password"
                label="Password"
                :type="showPasswordToggle ? 'text' : 'password'"
                :rules="passwordRules"
                outlined
                prepend-inner-icon="mdi-lock-outline"
                :append-icon="showPasswordToggle ? 'mdi-eye-off' : 'mdi-eye'"
                @click:append="showPasswordToggle = !showPasswordToggle"
                class="rounded-lg mb-4"
                background-color="grey lighten-4"
              ></v-text-field>

              <!-- OTP Section -->
              <v-expand-transition>
                <div v-if="otpRequested">
                  <v-alert
                    dense
                    type="info"
                    color="blue-grey"
                    class="mb-6 rounded-lg"
                    elevation="1"
                  >
                    <div class="d-flex align-center justify-space-between">
                      <span class="text-body-2">
                        Code sent to <strong>{{ email }}</strong>
                      </span>
                      <v-btn text small color="text--white" @click="editEmail">Edit</v-btn>
                    </div>
                  </v-alert>
                  <v-otp-input
                    v-model="otp"
                    length="6"
                    type="number"
                    class="mb-6 centered-otp"
                    :loading="loginInProgress"
                  ></v-otp-input>
                </div>
              </v-expand-transition>

              <!-- Action Buttons -->
              <div v-if="!otpRequested && !showPassword" class="d-flex flex-column gap-2">
                <v-btn
                  @click="requestOtp"
                  color="primary"
                  block
                  x-large
                  elevation="0"
                  class="rounded-lg"
                  :disabled="otpTimerRunning || loginInProgress"
                  :loading="loginInProgress"
                >
                  {{ otpTimerRunning ? `Wait ${otpTimer}s` : 'Request OTP' }}
                </v-btn>
                <v-btn
                  @click="requestMagicLink"
                  color="primary"
                  outlined
                  block
                  x-large
                  elevation="0"
                  class="rounded-lg"
                  :loading="loginInProgress"
                >
                  Send Magic Link
                </v-btn>
                <v-btn
                  @click="togglePasswordLogin"
                  text
                  color="primary"
                  class="mt-2"
                >
                  Use Password Instead
                </v-btn>
              </div>

              <!-- Password Login Button -->
              <v-btn
                v-if="showPassword"
                @click="loginWithPassword"
                color="primary"
                block
                x-large
                elevation="0"
                class="rounded-lg mb-3"
                :loading="loginInProgress"
                :disabled="!email || !password"
              >
                Sign In with Password
              </v-btn>
              <v-btn
                v-if="showPassword"
                text
                color="primary"
                @click="togglePasswordLogin"
                class="mt-2"
              >
                Use OTP or Magic Link Instead
              </v-btn>

              <!-- OTP Verify Button -->
              <v-btn
                v-if="otpRequested"
                @click="verifyOtp"
                color="primary"
                block
                x-large
                elevation="0"
                class="rounded-lg mb-3"
                :loading="loginInProgress"
                :disabled="otp.length !== 6"
              >
                Verify & Sign In
              </v-btn>

              <!-- Resend Timer -->
              <v-expand-transition>
                <div v-if="otpRequested" class="text-center mt-2">
                  <v-btn
                    v-if="!otpTimerRunning"
                    text
                    color="primary"
                    @click="requestOtp"
                  >
                    <v-icon left>mdi-refresh</v-icon>
                    Resend Code
                  </v-btn>
                  <span v-else class="text-caption grey--text">
                    Resend available in {{ otpTimer }}s
                  </span>
                </div>
              </v-expand-transition>

              <!-- Signup Link -->
              <div v-if="!otpRequested" class="text-center mt-4">
                <span class="text-body-2 grey--text text--darken-1">
                  Don’t have an account?
                  <router-link to="/signup" class="text-decoration-none primary--text font-weight-medium">
                    Sign Up
                  </router-link>
                </span>
              </div>

              <!-- Footer -->
              <v-divider class="my-6"></v-divider>
              <div class="text-center text-caption grey--text">
                <p>
                  By signing in, you agree to our
                  <a href="/terms" class="text-decoration-none primary--text">Terms</a>
                  and
                  <a href="/policy" class="text-decoration-none primary--text">Privacy Policy</a>
                </p>
                <p class="mt-2">© {{ new Date().getFullYear() }} {{ appName }} v{{ appVersion }}</p>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <v-snackbar
      v-model="snackbar.visible"
      :timeout="snackbar.timeout"
      :color="snackbar.color"
      bottom
      rounded="pill"
      elevation="6"
      class="mb-4"
    >
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.visible = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-app>
</template>

<script>
import { defaultApiCall, checkValidEmail } from '../utils/globalFunctions';
import { getDeviceId, getDeviceName } from '../utils/deviceUtils';
import { resetAllStores } from '@/store/resetAllStores';

import { useClientsStore } from '@/store/clients';
import { useInvoicesStore } from '@/store/invoices';
import { useCompanyStore } from '@/store/companies';
import { usePaymentStore } from '@/store/payments';
import { useTaxStore } from '@/store/taxes';
import { useTemplateStore } from '@/store/templates';
import { useNotificationsStore } from '@/store/notifications';
import { useDeviceStore } from '@/store/devices';
import { useSubscriptionStore } from '@/store/subscription';

export default {
  name: 'LoginPage',
  data() {
    return {
      email: '',
      password: '',
      otp: '',
      otpRequested: false,
      showPassword: false,
      showPasswordToggle: false,
      otpTimer: 300,
      otpTimerRunning: false,
      otpTimerInterval: null, // Store timer reference
      loginInProgress: false,
      snackbar: {
        visible: false,
        message: '',
        timeout: 3000,
        color: '',
      },
      appName: 'InvoiceSnap',
      appVersion: process.env.VUE_APP_VERSION,
      deviceId: '',
      deviceName: '',
      emailRules: [
        (v) => !!v || 'Email is required',
        (v) => checkValidEmail(v) || 'Invalid email format',
      ],
      passwordRules: [
        (v) => !!v || 'Password is required',
        (v) => (v && v.length >= 6) || 'Password must be at least 6 characters',
      ],
      serverWaitTime: 300,
    };
  },
  async created() {
    this.deviceId = await getDeviceId();
    this.deviceName = await getDeviceName();
  },
  methods: {
    showSnackbar(type, message) {
      const colors = { success: 'success', warning: 'warning', error: 'error' };
      this.snackbar.message = message;
      this.snackbar.color = colors[type] || 'info';
      this.snackbar.visible = true;
    },
    async requestOtp() {
      // Prevent requesting OTP if timer is running
      if (this.otpTimerRunning) {
        this.showSnackbar('warning', `Please wait ${this.otpTimer} seconds before requesting a new code.`);
        return;
      }

      if (!this.email || !checkValidEmail(this.email)) {
        this.showSnackbar('error', 'Please enter a valid email!');
        return;
      }

      this.loginInProgress = true;
      const formData = new FormData();
      formData.append('email', this.email);

      try {
        const response = await defaultApiCall({
          method: 'POST',
          url: '/Login/requestOtp',
          data: formData,
        });

        this.loginInProgress = false;
        if (response.status) {
          this.otpRequested = true;
          this.startOtpTimer();
          this.showSnackbar('success', 'Verification code sent to your email!');
        } else {
          this.showSnackbar('error', response.message || 'Error requesting OTP!');
        }
        if (response.waitTime) {
          this.otpRequested = true;
          this.serverWaitTime = response.waitTime || 300;
          this.startOtpTimer();
        }
      } catch (error) {
        this.loginInProgress = false;
        this.showSnackbar('error', error.message || 'Server error');
      }
    },
    async verifyOtp() {
      if (this.otp.length !== 6) {
        this.showSnackbar('error', 'Please enter a 6-digit code!');
        return;
      }

      this.loginInProgress = true;
      const formData = new FormData();
      formData.append('email', this.email);
      formData.append('otp', this.otp);
      formData.append('deviceId', this.deviceId);
      formData.append('deviceName', this.deviceName);

      try {
        const response = await defaultApiCall({
          method: 'POST',
          url: '/Login/validateOtp',
          data: formData,
        });

        this.loginInProgress = false;
        if (response.status) {
          this.showSnackbar('success', 'Login successful!');
          this.$store.dispatch('login', { user: response.user, token: response.token });
          this.$store.dispatch('deviceIdentifier', this.deviceId);
          resetAllStores().then(() => {
            useSubscriptionStore().setUserId(response.user.user_id);
            useClientsStore().setUserId(response.user.user_id);
            useInvoicesStore().setUserId(response.user.user_id);
            useCompanyStore().setUserId(response.user.user_id);
            usePaymentStore().setUserId(response.user.user_id);
            useTaxStore().setUserId(response.user.user_id);
            useTemplateStore().setUserId(response.user.user_id);
            useNotificationsStore().setUserId(response.user.user_id);
            useDeviceStore().setUserId(response.user.user_id);

            this.$router.push('/user/dashboard');
          });

        } else {
          this.showSnackbar('error', response.message || 'Invalid verification code');
        }
      } catch (error) {
        this.loginInProgress = false;
        this.showSnackbar('error', error.message || 'Server error');
      }
    },
    async requestMagicLink() {
      if (!this.email || !checkValidEmail(this.email)) {
        this.showSnackbar('error', 'Please enter a valid email!');
        return;
      }

      this.loginInProgress = true;
      const formData = new FormData();
      formData.append('email', this.email);
      formData.append('deviceId', this.deviceId);
      formData.append('deviceName', this.deviceName);

      try {
        const response = await defaultApiCall({
          method: 'POST',
          url: '/Login/requestMagicLink',
          data: formData,
        });

        this.loginInProgress = false;
        if (response.status) {
          this.showSnackbar('success', 'Magic link sent to your email! Check your inbox.');
        } else {
          this.showSnackbar('error', response.message || 'Error sending magic link!');
        }
      } catch (error) {
        this.loginInProgress = false;
        this.showSnackbar('error', error.message || 'Server error');
      }
    },
    async loginWithPassword() {
      if (!this.email || !this.password) {
        this.showSnackbar('error', 'Please enter email and password!');
        return;
      }

      this.loginInProgress = true;
      const formData = new FormData();
      formData.append('email', this.email);
      formData.append('password', this.password);
      formData.append('deviceId', this.deviceId);
      formData.append('deviceName', this.deviceName);

      try {
        const response = await defaultApiCall({
          method: 'POST',
          url: '/Login/loginWithPassword',
          data: formData,
        });

        this.loginInProgress = false;
        if (response.status) {
          this.showSnackbar('success', 'Login successful!');
          this.$store.dispatch('login', { user: response.user, token: response.token });
          this.$store.dispatch('deviceIdentifier', this.deviceId);
          resetAllStores().then(() => {
            useSubscriptionStore().setUserId(response.user.user_id);
            useClientsStore().setUserId(response.user.user_id);
            useInvoicesStore().setUserId(response.user.user_id);
            useCompanyStore().setUserId(response.user.user_id);
            usePaymentStore().setUserId(response.user.user_id);
            useTaxStore().setUserId(response.user.user_id);
            useTemplateStore().setUserId(response.user.user_id);
            useNotificationsStore().setUserId(response.user.user_id);
            useDeviceStore().setUserId(response.user.user_id);

            this.$router.push('/user/dashboard');
          });
        } else {
          this.showSnackbar('error', response.message || 'Invalid email or password');
        }
      } catch (error) {
        this.loginInProgress = false;
        this.showSnackbar('error', error.message || 'Server error');
      }
    },
    startOtpTimer() {
      // Clear any existing timer
      if (this.otpTimerInterval) {
        clearInterval(this.otpTimerInterval);
      }

      this.otpTimer = this.serverWaitTime;
      this.otpTimerRunning = true;
      this.otpTimerInterval = setInterval(() => {
        this.otpTimer -= 1;
        if (this.otpTimer <= 0) {
          this.otpTimerRunning = false;
          clearInterval(this.otpTimerInterval);
          this.otpTimerInterval = null;
        }
      }, 1000);
    },
    editEmail() {
      // Clear timer when editing email
      if (this.otpTimerInterval) {
        clearInterval(this.otpTimerInterval);
        this.otpTimerInterval = null;
      }
      this.otpRequested = false;
      this.otp = '';
      this.showPassword = false;
      this.otpTimerRunning = false;
      this.otpTimer = this.serverWaitTime;
    },
    togglePasswordLogin() {
      // Clear timer when toggling to password login
      if (this.otpTimerInterval) {
        clearInterval(this.otpTimerInterval);
        this.otpTimerInterval = null;
      }
      this.showPassword = !this.showPassword;
      this.otpRequested = false;
      this.otp = '';
      this.otpTimerRunning = false;
      this.otpTimer = this.serverWaitTime;
    },
  },
};
</script>

<style scoped>
.app-background {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
}
.professional-card {
  border: 1px solid rgba(0, 0, 0, 0.05);
  background: white;
  transition: transform 0.2s ease;
}
.professional-card:hover {
  transform: translateY(-2px);
}
.centered-otp {
  justify-content: center;
}
.v-btn {
  letter-spacing: 0.5px;
  text-transform: none;
  font-weight: 500;
}
.gap-2 {
  gap: 8px;
}
a:hover {
  color: #1976d2 !important;
}
</style>